import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { PAYMENT_URL, PAYMENT_LABEL } from "@/lib/config";
import { Overline, EASE } from "./anim";

const TOPICS = [
  "Business strategy",
  "Investment opportunity analysis",
  "Deal evaluation",
  "Wealth-building strategy",
  "Entrepreneurial growth",
  "Something else",
];

const EMPTY = { first_name: "", last_name: "", email: "", topic: "", preferred_time: "", message: "" };

export default function BookingModal({ open, onClose }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const close = () => {
    onClose();
    setTimeout(() => {
      setDone(false);
      setForm(EMPTY);
    }, 400);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/consultations", form);
      setDone(true);
    } catch (err) {
      toast.error("Could not submit your request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="booking-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[100] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center sm:p-5"
          onClick={close}
        >
          <motion.div
            initial={{ y: 48, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 48, opacity: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="max-h-[92vh] w-full max-w-xl overflow-y-auto border border-white/10 bg-[#0F0F12] p-7 sm:p-10"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Schedule a consultation"
          >
            <div className="flex items-start justify-between gap-6">
              <div>
                <Overline>Consultation</Overline>
                <h3 className="mt-3 text-2xl font-light tracking-tight text-[#F9F8F6]">
                  Schedule a consultation
                </h3>
                <div className="mt-4 flex items-center gap-3">
                  <img
                    src="/assets/founder-editorial.png"
                    alt="Jenn Woodward, founder of Kingdom Wealth Enterprises"
                    className="h-10 w-10 rounded-full border border-[#C5A059]/40 object-cover object-top"
                  />
                  <p className="text-xs font-light leading-snug text-[#A1A1AA]">
                    You will speak directly with Jenn,
                    <br />
                    not a sales team.
                  </p>
                </div>
              </div>
              <button data-testid="booking-close-button" onClick={close} aria-label="Close" className="mt-1 text-[#A1A1AA] transition-colors hover:text-[#C5A059]">
                <X size={20} />
              </button>
            </div>

            {done ? (
              <div data-testid="booking-success" className="mt-10 border border-[#C5A059]/30 bg-[#C5A059]/[0.05] p-8 text-center">
                <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-[#C5A059]/50 text-[#C5A059]">
                  <Check size={20} />
                </span>
                <p className="mt-5 text-lg font-light text-[#F9F8F6]">Request received.</p>
                <p className="mx-auto mt-2 max-w-sm text-sm font-light leading-relaxed text-[#A1A1AA]">
                  Thank you, {form.first_name}. We will review your goals and reach out shortly to confirm your
                  consultation.
                </p>
                <button data-testid="booking-done-button" onClick={close} className="btn-ghost mt-7">
                  Done
                </button>
                {PAYMENT_URL && (
                  <a
                    data-testid="booking-pay-link"
                    href={PAYMENT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn-gold ml-3 mt-7"
                  >
                    {PAYMENT_LABEL}
                  </a>
                )}
              </div>
            ) : (
              <form data-testid="booking-form" onSubmit={submit} className="mt-8 space-y-4">
                <p className="text-sm font-light leading-relaxed text-[#A1A1AA]">
                  Tell us a little about you and what you are working toward. We will take it from there.
                </p>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="booking-first-name" className="kwe-label">First name</label>
                    <input id="booking-first-name" data-testid="booking-first-name-input" className="kwe-input" required value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} autoComplete="given-name" />
                  </div>
                  <div>
                    <label htmlFor="booking-last-name" className="kwe-label">Last name</label>
                    <input id="booking-last-name" data-testid="booking-last-name-input" className="kwe-input" required value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} autoComplete="family-name" />
                  </div>
                </div>
                <div>
                  <label htmlFor="booking-email" className="kwe-label">Email address</label>
                  <input id="booking-email" data-testid="booking-email-input" type="email" className="kwe-input" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} autoComplete="email" />
                </div>
                <div>
                  <label htmlFor="booking-topic" className="kwe-label">What would you like to discuss?</label>
                  <select id="booking-topic" data-testid="booking-topic-select" className="kwe-input appearance-none bg-[#0F0F12]" required value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })}>
                    <option value="" disabled>Select a topic</option>
                    {TOPICS.map((t) => (
                      <option key={t} value={t}>{t}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="booking-time" className="kwe-label">Preferred time (optional)</label>
                  <input id="booking-time" data-testid="booking-time-input" className="kwe-input" value={form.preferred_time} onChange={(e) => setForm({ ...form, preferred_time: e.target.value })} placeholder="e.g., Weekday afternoons, Eastern Time" />
                </div>
                <div>
                  <label htmlFor="booking-message" className="kwe-label">Anything we should know? (optional)</label>
                  <textarea id="booking-message" data-testid="booking-message-input" rows={3} className="kwe-input resize-none" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Your goals, or the opportunity you are evaluating" />
                </div>
                <button data-testid="booking-submit-button" type="submit" disabled={submitting} className="btn-gold w-full disabled:opacity-60">
                  {submitting ? "Submitting..." : "Request Consultation"}
                </button>
                <p className="text-center text-[11px] font-light text-[#71717A]">
                  Your information is kept confidential and never shared.
                </p>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
